import {
  DevicesBlockColPin,
  DevicesTitle,
  GaugeCardsGrid,
  HiveAreaItem,
  HiveBodySummaryWrapper,
} from 'features/hive/ui/HiveBody/styles.ts';
import {HiveBodyHappiness} from 'features/hive/ui/HiveBody/HiveBodyHappiness.tsx';
import {HiveBodyConnect} from 'features/hive/ui/HiveBody/HiveBodyConnect.tsx';
import {HiveBodyArchive} from 'features/hive/ui/HiveBody/HiveBodyArchive.tsx';
import HiveBodyChart from 'features/hive/ui/HiveBody/HiveBodyChart.tsx';
import {TemperatureIcon} from 'shared/ui/icons/temperatureIcon.tsx';
import {HumidityIcon} from 'shared/ui/icons/humidityIcon.tsx';
import {HiveWeightIcon} from 'shared/ui/icons/hiveWeightIcon.tsx';
import {HiveBodyUterus} from 'features/hive/ui/HiveBody/HiveBodyUterus.tsx';
import {HiveBodyState} from 'features/hive/ui/HiveBody/HiveBodyState.tsx';
import {HiveBodyPhotos} from 'features/hive/ui/HiveBody/HiveBodyPhotos.tsx';
import {useResize} from '../../../../app/lib/hooks/useResize.ts';
import Co2Chapter from 'shared/ui/icons/chapterIcons/co2Chapter.tsx';
import {chartValue} from 'widgets/chart/model/types';
import {useLocation, useParams} from 'react-router-dom';
import {useCurrentHiveStore} from 'entities/currentHive';
import {CalendarCalendar} from 'features/calendar/index.ts';
import {useCalendarStore} from 'entities/calendar/index.ts';
import {useApiariesStore} from 'entities/apiaries/index.ts';
import {useDevicesStore} from 'entities/devices/model/store';
import {useNewDeviceModalStore} from 'entities/newDeviceModal';
import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {HiveBodyRent} from './HiveBodyRent.tsx';
import {SharedHive} from 'entities/hives/model/types.ts';
import {HiveAudios} from '../../../../widgets/hiveAudios';
import {$api} from 'shared/api';
import BatteryIcon from 'shared/ui/icons/batteryIcon.tsx';
import {MoreIcon} from 'shared/ui/icons/moreIcon.tsx';
import {Button} from '@atomaro/ui-kit';
import styled from 'styled-components';
import {COLORS} from 'shared/consts';
import {toRem} from 'shared/helpers';
import {useHivesSoundsStore} from 'entities/hiveSound/index.ts';
import {DeviceInfoModal} from 'widgets/deviceInfoModal';
import {CARDS} from 'widgets/devicesInfo/consts/cards.consts.ts';
import {SensorCard, SensorStatus} from 'features/hive/ui/HiveBody/SensorCard/SensorCard.tsx';
import {RoofIcon} from 'shared/ui/icons/roof-icon.tsx';
import {PositionIcon} from 'shared/ui/icons/positionIcon.tsx';
import {utcToUserDate} from 'features/calendar/helpers/dates.ts';
import {format} from 'date-fns';

function toSeries(arr?: {date: string; value: number}[]): chartValue[] {
  return (arr ?? []).map((p) => ({
    x: new Date(p.date).getTime(),
    y: p.value,
  }));
}
function lastTimeStr(data?: {date: string; value: number}[]): string {
  if (!data || data.length === 0) return '';
  const last = new Date(data[data.length - 1].date);
  const hh = String(last.getHours()).padStart(2, '0');
  const mm = String(last.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}
function avgValue(data?: {date: string; value: number}[]): number {
  if (!data || data.length === 0) return 0;
  const s = data.reduce((acc, p) => acc + (p?.value ?? 0), 0);
  return Number((s / data.length).toFixed(1));
}

function determineTilt(value: number) {
  const map: Record<number, {status: SensorStatus; text: string}> = {
    1: {status: 'normal', text: 'Стоит ровно'},
    0: {status: 'warning', text: 'Наклон'},
  };
  const result = map[value] ?? {status: 'danger', text: 'Упал'};
  const tiltSensorStatus = result.status;
  const tiltStatusText = result.text;

  return {tiltSensorStatus, tiltStatusText};
}

function avgGain(data?: {date: string; value: number}[]): number {
  if (!data || data.length < 2) return 0;
  let sumGain = 0;
  let count = 0;
  for (let i = 1; i < data.length; i++) {
    const diff = (data[i]?.value ?? 0) - (data[i - 1]?.value ?? 0);
    sumGain += diff;
    count++;
  }
  const avg = sumGain / count;
  return Number(avg.toFixed(2));
}

type ApiPoint = {time?: string; timestamp?: string; date?: string; value?: number | string | null};
type HiveAnalyticsResponse = Partial<
  Record<'temperature' | 'humidity' | 'co2' | 'weight' | 'roofOpen' | 'tiltStatus', ApiPoint[]>
>;

function toNumeric(val: string | number | null | undefined): number | null {
  if (val === null || val === undefined) return null;
  const num = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(num) ? num : null;
}

function mapApi(arr: ApiPoint[] | undefined): {date: string; value: number}[] {
  return (arr ?? [])
    .map((p) => {
      const date = p.time ?? p.timestamp ?? p.date ?? null;
      const value = toNumeric(p.value);
      if (!date || value === null) return null;
      return {date, value};
    })
    .filter(
      (p): p is {date: string; value: number} =>
        p !== null && !Number.isNaN(new Date(p.date).getTime())
    );
}

function iso24HoursAgo(): string {
  const d = new Date(Date.now() - 24 * 3600_000);
  return d.toISOString();
}

function isoNow(): string {
  return new Date().toISOString();
}

const formatVoltage = (v?: number | null) => {
  if (v == null) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  return `${n.toFixed(1).replace('.', ',')} В`;
};

const batteryColors = (v?: number | null) => {
  const n = Number(v);
  if (Number.isNaN(n)) {
    return {
      color: 'var(--fg-muted, rgba(16, 24, 40, 0.55))',
      bg: 'rgba(16, 24, 40, 0.06)',
    };
  }
  if (n >= 2.8) {
    return {
      color: 'var(--badge-secondary-success-label-color, #019F3C)',
      bg: 'var(--badge-secondary-success-bg-color, rgba(1, 159, 60, 0.1))',
    };
  }
  if (n >= 1.5) {
    return {
      color: 'var(--badge-secondary-warning-label-color, rgba(215, 141, 14, 1))',
      bg: 'var(--badge-secondary-warning-bg-color, rgba(253, 166, 16, 0.1))',
    };
  }
  return {
    color: 'var(--error-on-container, rgba(255, 38, 38, 1))',
    bg: 'var(--error-container-default, rgba(255, 38, 38, 0.1))',
  };
};

const DevicesSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${COLORS.BG.SURFACE1};
  border-radius: ${toRem(16)};
  padding: ${toRem(16)};
  min-height: ${toRem(200)};
  height: 100%;
  flex: 1;
`;

const DevicesTitleUfa = styled(DevicesTitle)`
  padding: 0 0 ${toRem(8)} 0;
`;

const DeviceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${toRem(8)};
`;

const DeviceNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${toRem(8)};
  flex: 1;
`;

const DeviceName = styled.p`
  margin: 0;
  color: ${COLORS.FG.DEFAULT};
  font-size: ${toRem(14)};
  font-weight: 500;
  line-height: ${20 / 14};
`;

const KebabIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: ${toRem(4)};

  svg {
    width: ${toRem(20)};
    height: ${toRem(20)};
  }
`;

const DeviceBlockStatic = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${toRem(16)} 0;
  min-width: 0;
  position: relative;

  &:not(:last-of-type) {
    border-bottom: 1px solid #585d6926;
  }
`;

const DeviceContextMenu = styled.div`
  position: absolute;
  top: ${toRem(40)};
  right: 0;
  z-index: 1000;
  background: ${COLORS.BG.PAGE};
  border-radius: ${toRem(8)};
  box-shadow:
    0px 32px 32px 0px rgba(88, 93, 105, 0.05),
    0px 0px 32px 0px rgba(88, 93, 105, 0.1);
  padding: ${toRem(8)} 0;
  min-width: ${toRem(140)};
`;

const DeviceContextMenuItem = styled.div`
  padding: ${toRem(8)} ${toRem(12)};
  color: ${COLORS.FG.DEFAULT};
  font-size: ${toRem(14)};
  font-weight: 400;
  line-height: ${20 / 14};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(234, 234, 236, 1);
  }

  &:last-of-type {
    color: var(--error-default, rgba(255, 38, 38, 1));
  }
`;

type HiveBodySummaryProps = {
  findedShared: SharedHive | null;
};

export const HiveBodySummary: FC<HiveBodySummaryProps> = ({findedShared}) => {
  const {id} = useParams<{id: string}>();

  const audioRecords = useHivesSoundsStore((s) => s.items);
  const hiveAudioRecords = useMemo(
    () => audioRecords.filter((ar) => ar.hiveId === id),
    [audioRecords, id]
  );

  const isSmart = useCurrentHiveStore((s) => s.currentHive)?.isSmart || false;

  const {isScreenLg} = useResize();
  const currentPath = useLocation().pathname;
  const events = useCalendarStore((s) => s.eventsByHive);
  const getEventsFromServerByHive = useCalendarStore((s) => s.getEventsFromServerByHive);
  const apiaryId = useApiariesStore((state) => state.currentApiaryId);
  const devices = useDevicesStore((s) => s.devices);
  const getDevices = useDevicesStore((s) => s.getDevicesFromServer);
  const detachDevice = useDevicesStore((s) => s.detachDevice);
  const currentHive = useCurrentHiveStore((s) => s.currentHive);
  const openConnect = useNewDeviceModalStore((s) => s.open);
  const setDeviceModalState = useDevicesStore((s) => s.setModalState);

  const lastApiaryId = useRef<string | null>(null);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRefs = useRef<{[key: string]: HTMLDivElement | null}>({});

  const [temperature, setTemperature] = useState<{date: string; value: number}[]>([]);
  const [humidity, setHumidity] = useState<{date: string; value: number}[]>([]);
  const [co2, setCo2] = useState<{date: string; value: number}[]>([]);
  const [weight, setWeight] = useState<{date: string; value: number}[]>([]);
  const [roofOpen, setRoofOpen] = useState<{date: string; value: number}[]>([]);
  const [tiltStatus, setTiltStatus] = useState<{date: string; value: number}[]>([]);

  const [isDeviceInfoOpen, setIsDeviceInfoOpen] = useState(false);
  const [selectedDeviceCard, setSelectedDeviceCard] = useState<(typeof CARDS)[number]>();

  useEffect(() => {
    if (apiaryId && id && lastApiaryId.current !== apiaryId) {
      lastApiaryId.current = apiaryId;
      getEventsFromServerByHive({apiaryId, hiveIds: [id]});
    }
  }, [apiaryId, events, id]);

  useEffect(() => {
    if (!apiaryId || !id) {
      setTemperature([]);
      setHumidity([]);
      setCo2([]);
      setWeight([]);
      setRoofOpen([]);
      setTiltStatus([]);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      try {
        const response = await $api.get<HiveAnalyticsResponse>('/collector/analytics/summary', {
          params: {
            hiveId: id,
            from: iso24HoursAgo(),
            to: isoNow(),
            interval: 'hour',
          },
        });
        if (!cancelled) {
          setTemperature(mapApi(response?.data?.temperature));
          setHumidity(mapApi(response?.data?.humidity));
          setCo2(mapApi(response?.data?.co2));
          setWeight(mapApi(response?.data?.weight));
          setRoofOpen(mapApi(response?.data?.roofOpen));
          setTiltStatus(mapApi(response?.data?.tiltStatus));
        }
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setTemperature([]);
          setHumidity([]);
          setCo2([]);
          setWeight([]);
          setRoofOpen([]);
          setTiltStatus([]);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [apiaryId, id]);

  useEffect(() => {
    if (apiaryId) getDevices({apiaryId});
  }, [apiaryId, getDevices]);

  const hiveId = currentHive?.id || '';
  const hiveNumber = currentHive?.number || '';
  const filteredDevices = useMemo(
    () => devices.filter((d) => (d.hiveId || '') === hiveId),
    [devices, hiveId]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId) {
        const menuElement = menuRefs.current[openMenuId];
        const target = event.target as Node;
        if (menuElement && !menuElement.contains(target)) {
          setOpenMenuId(null);
        }
      }
    };

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const handleMenuToggle = (deviceId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === deviceId ? null : deviceId);
  };

  const handleDetach = async (deviceId: string) => {
    try {
      await detachDevice(deviceId);
      setOpenMenuId(null);
    } catch (error) {
      console.error('Failed to detach device:', error);
    }
  };

  const isRoofOpen = Number(roofOpen[roofOpen.length - 1]?.value ?? 0);
  const tiltValue = tiltStatus[0]?.value ?? 1;
  const {tiltSensorStatus, tiltStatusText} = determineTilt(tiltValue);

  const tempSeries = useMemo(() => toSeries(temperature), [temperature]);
  const humidSeries = useMemo(() => toSeries(humidity), [humidity]);
  const co2Series = useMemo(() => toSeries(co2), [co2]);
  const weightSeries = useMemo(() => toSeries(weight), [weight]);

  const tempTime = lastTimeStr(temperature);
  const humidTime = lastTimeStr(humidity);
  const co2Time = lastTimeStr(co2);
  const weightTime = lastTimeStr(weight);

  const tempAvg = useMemo(() => avgValue(temperature), [temperature]);
  const humidAvg = useMemo(() => avgValue(humidity), [humidity]);
  const co2Avg = useMemo(() => avgValue(co2), [co2]);
  const weightAvg = useMemo(() => avgValue(weight), [weight]);
  const weightGainAvg = useMemo(() => avgGain(weight), [weight]);

  const hasTemperatureData = temperature.length > 0;
  const hasHumidityData = humidity.length > 0;
  const hasCo2Data = co2.length > 0;
  const hasWeightData = weight.length > 0;

  return (
    <>
      <HiveBodySummaryWrapper $withGaugeCards={!!currentHive?.isSmart}>
        <HiveAreaItem $area="gauge">
          <HiveBodyHappiness />
        </HiveAreaItem>

        {currentHive?.isSmart && (
          <HiveAreaItem $area="gaugeCards">
            <GaugeCardsGrid>
              <SensorCard
                icon={<RoofIcon />}
                title="Крышка"
                time={format(utcToUserDate(roofOpen[0]?.date) ?? new Date(), 'HH:mm')}
                statusText={isRoofOpen ? 'Открыта' : 'Закрыта'}
                status={isRoofOpen ? 'danger' : 'normal'}
              />
              <SensorCard
                icon={<PositionIcon />}
                title="Положение"
                time={format(utcToUserDate(tiltStatus[0]?.date) ?? new Date(), 'HH:mm')}
                statusText={tiltStatusText}
                status={tiltSensorStatus}
              />
              <SensorCard
                icon={<RoofIcon />}
                title="Геопозиция"
                time="12:48"
                statusText="Посмотреть на карте"
                status={'normal'}
              />
              <SensorCard
                icon={<RoofIcon />}
                title="Аудиозаписи"
                time="12:48"
                statusText="Спокойные"
                status={'normal'}
              />
            </GaugeCardsGrid>
          </HiveAreaItem>
        )}

        {(isScreenLg || isSmart) && (
          <HiveAreaItem $area="temp">
            <HiveBodyChart
              icon={<TemperatureIcon />}
              title={'Температура'}
              lastUpdatedTime={hasTemperatureData ? tempTime : undefined}
              to={`${currentPath}/analytics/temperature`}
              type={'sparkline'}
              data={hasTemperatureData ? tempSeries : []}
              tooltipText={[{title: 'Температура', dim: '°C', isSign: false}]}
              seriesLabel="Температура"
              averages={hasTemperatureData ? [{value: tempAvg, unit: '°C', status: 'green'}] : []}
            />
          </HiveAreaItem>
        )}
        {(isScreenLg || isSmart) && (
          <HiveAreaItem $area="humid">
            <HiveBodyChart
              icon={<HumidityIcon />}
              title={'Влажность'}
              lastUpdatedTime={hasHumidityData ? humidTime : undefined}
              to={`${currentPath}/analytics/humidity`}
              type={'sparkline'}
              data={hasHumidityData ? humidSeries : []}
              tooltipText={[{title: 'Влажность', dim: '%', isSign: false}]}
              seriesLabel="Влажность"
              averages={
                hasHumidityData ? [{value: Math.round(humidAvg), unit: '%', status: 'green'}] : []
              }
            />
          </HiveAreaItem>
        )}
        {(isScreenLg || isSmart) && (
          <HiveAreaItem $area="co2">
            <HiveBodyChart
              icon={<Co2Chapter />}
              title={'CO₂'}
              lastUpdatedTime={hasCo2Data ? co2Time : undefined}
              to={`${currentPath}/analytics/co2`}
              type={'sparkline'}
              data={hasCo2Data ? co2Series : []}
              tooltipText={[{title: 'CO₂', dim: 'ppm', isSign: false}]}
              seriesLabel="CO₂"
              averages={
                hasCo2Data ? [{value: Math.round(co2Avg), unit: 'ppm', status: 'green'}] : []
              }
            />
          </HiveAreaItem>
        )}
        {(isScreenLg || isSmart) && (
          <HiveAreaItem $area="weight">
            <HiveBodyChart
              icon={<HiveWeightIcon />}
              title={'Масса улья'}
              lastUpdatedTime={hasWeightData ? weightTime : undefined}
              to={`${currentPath}/analytics/weight`}
              type={'column'}
              data={hasWeightData ? weightSeries : []}
              tooltipText={[
                {title: 'Масса улья', dim: 'кг', isSign: false},
                {title: 'Привес', dim: 'кг', isSign: true},
              ]}
              seriesLabel="Масса улья"
              showDiff
              averages={
                hasWeightData
                  ? [
                      {value: weightAvg, unit: 'кг', status: 'green'},
                      {value: weightGainAvg, unit: 'кг', status: 'green'},
                    ]
                  : []
              }
            />
          </HiveAreaItem>
        )}

        <HiveAreaItem $area="devices">
          {filteredDevices.length > 0 ? (
            <DevicesSectionWrapper>
              <DevicesTitleUfa>Подключенные устройства</DevicesTitleUfa>

              {filteredDevices.map((d) => {
                const v = (d as any)?.batteryVoltage as number | undefined;
                const {color, bg} = batteryColors(v);

                return (
                  <DeviceBlockStatic
                    key={d.id}
                    ref={(el) => {
                      menuRefs.current[d.id] = el;
                    }}
                  >
                    <DeviceRow>
                      <DeviceNameWrapper>
                        <DeviceName>{d.name || 'Устройство без названия'}</DeviceName>
                        <DevicesBlockColPin $color={color} $bg={bg}>
                          <BatteryIcon fill={color} /> {formatVoltage(v)}
                        </DevicesBlockColPin>
                      </DeviceNameWrapper>
                      <KebabIconWrapper onClick={(e) => handleMenuToggle(d.id, e)}>
                        <MoreIcon />
                      </KebabIconWrapper>
                    </DeviceRow>
                    {openMenuId === d.id && (
                      <DeviceContextMenu>
                        <DeviceContextMenuItem
                          onClick={() => {
                            setOpenMenuId(null);
                            setDeviceModalState({isOpen: true, name: d.name, id: d.id});
                          }}
                        >
                          Изменить
                        </DeviceContextMenuItem>
                        <DeviceContextMenuItem
                          onClick={() => {
                            setOpenMenuId(null);
                            const card = CARDS.find((card) => card.registryName === d.registryName);
                            if (!card) return;
                            setSelectedDeviceCard(card);
                            setIsDeviceInfoOpen(true);
                          }}
                        >
                          Подробнее
                        </DeviceContextMenuItem>
                        <DeviceContextMenuItem
                          onClick={() => {
                            handleDetach(d.id);
                          }}
                        >
                          Отключить
                        </DeviceContextMenuItem>
                      </DeviceContextMenu>
                    )}
                  </DeviceBlockStatic>
                );
              })}

              <Button
                size={'m'}
                onClick={() => openConnect(hiveId, hiveNumber)}
                label={'Подключить ещё'}
                style={{width: '100%', margin: 'auto auto 20px'}}
              />
            </DevicesSectionWrapper>
          ) : (
            <HiveBodyConnect />
          )}
        </HiveAreaItem>

        <HiveAreaItem $area="state">
          <HiveBodyState />
        </HiveAreaItem>
        <HiveAreaItem $area="queen">
          <HiveBodyUterus />
        </HiveAreaItem>
        {!!hiveAudioRecords.length && (
          <HiveAreaItem $area="audio">
            <HiveAudios isWidget audioRecords={hiveAudioRecords.slice(0, 2)} />
          </HiveAreaItem>
        )}
        <HiveAreaItem $area="events">
          <CalendarCalendar
            activeDate={new Date()}
            events={events}
            isVidget
            onChangeFilters={() => null}
            onEventDrop={() => null}
            onEventResize={() => null}
          />
        </HiveAreaItem>
        <HiveAreaItem $area="photos">
          <HiveBodyPhotos />
        </HiveAreaItem>
        <HiveAreaItem $area="archive">
          <HiveBodyRent findedShared={findedShared} isScreenLg={isScreenLg} />
          <HiveBodyArchive isScreenLg={isScreenLg} />
        </HiveAreaItem>
      </HiveBodySummaryWrapper>

      <DeviceInfoModal
        open={isDeviceInfoOpen}
        onClose={() => setIsDeviceInfoOpen(false)}
        device={selectedDeviceCard}
      />
    </>
  );
};
